import React, { useEffect, useState } from "react";
import {
  Alert, Button, Card, Checkbox, Col, DatePicker, Form,
  Input, InputNumber, Row, Select, Space, Spin, Table, Tag, Typography,
} from "antd";
import { SaveOutlined, ArrowLeftOutlined, BulbOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { TextArea } = Input;
const API_URL = process.env.REACT_APP_API_URL;

const ESTADOS = [
  "Sembrado", "EnCrecimiento", "EnFloracion", "ListoCosechar", "Cosechado", "Perdido",
];
const ESTADO_LABEL = {
  Sembrado: "Sembrado", EnCrecimiento: "En crecimiento", EnFloracion: "En floración",
  ListoCosechar: "Listo para cosechar", Cosechado: "Cosechado", Perdido: "Perdido",
};

const TIPO_COLOR = {
  "Preparación del terreno": "#8B4513",
  Siembra: "green", Fertilización: "blue", Riego: "cyan", Poda: "orange",
  ControlPlagas: "red", Fumigación: "volcano", Cosecha: "gold", Aporque: "lime", Otro: "default",
};

export default function FrmCultivoForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const esEdicion = Boolean(id);

  const [form] = Form.useForm();
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const [tipos, setTipos] = useState([]);
  const [productos, setProductos] = useState([]);
  const [tipoSeleccionado, setTipoSeleccionado] = useState(null);
  const [generarActividades, setGenerarActividades] = useState(true);

  // Cargar tipos de cultivo y productos
  useEffect(() => {
    const cargarCatalogos = async () => {
      try {
        const [tRes, pRes] = await Promise.all([
          axios.get(`${API_URL}/TipoCultivo`),
          axios.get(`${API_URL}/Producto`),
        ]);
        const tLista = Array.isArray(tRes.data?.$values) ? tRes.data.$values
          : Array.isArray(tRes.data) ? tRes.data : [];
        const pLista = Array.isArray(pRes.data?.$values) ? pRes.data.$values
          : Array.isArray(pRes.data) ? pRes.data : [];
        setTipos(tLista);
        setProductos(pLista);
      } catch { /* silencioso */ }
    };
    cargarCatalogos();
  }, []);

  // Si edición, cargar datos del cultivo
  useEffect(() => {
    if (!esEdicion) return;
    const cargar = async () => {
      try {
        setCargando(true);
        const resp = await axios.get(`${API_URL}/Cultivo/${id}`);
        const d = resp.data;
        form.setFieldsValue({
          nombre: d.nombre,
          tipoCultivoId: d.tipoCultivoId,
          lote: d.lote,
          areaHectareas: d.areaHectareas,
          fechaSiembra: d.fechaSiembra ? dayjs(d.fechaSiembra) : null,
          fechaEstimadaCosecha: d.fechaEstimadaCosecha ? dayjs(d.fechaEstimadaCosecha) : null,
          estado: d.estado,
          productoSemillaId: d.productoSemillaId,
          cantidadSemilla: d.cantidadSemilla,
          observaciones: d.observaciones,
        });
        if (d.tipoCultivoId) {
          const tipo = tipos.find(t => t.id === d.tipoCultivoId);
          if (tipo) setTipoSeleccionado(tipo);
        }
      } catch {
        setResultado({ tipo: "error", texto: "No se pudo cargar el cultivo." });
      } finally {
        setCargando(false);
      }
    };
    if (tipos.length > 0) cargar();
  }, [id, esEdicion, tipos]);

  const onTipoCultivoChange = (val) => {
    const tipo = tipos.find(t => t.id === val);
    setTipoSeleccionado(tipo ?? null);
    // Autocompletar fecha estimada cosecha
    if (tipo?.diasEstimadosCosecha) {
      const fechaSiembra = form.getFieldValue("fechaSiembra");
      if (fechaSiembra) {
        form.setFieldValue("fechaEstimadaCosecha",
          fechaSiembra.add(tipo.diasEstimadosCosecha, "day"));
      }
    }
  };

  const onFechaSiembraChange = (val) => {
    if (val && tipoSeleccionado?.diasEstimadosCosecha) {
      form.setFieldValue("fechaEstimadaCosecha",
        val.add(tipoSeleccionado.diasEstimadosCosecha, "day"));
    }
  };

  const onGuardar = async () => {
    try {
      const values = await form.validateFields();
      setGuardando(true);
      setResultado(null);

      const body = {
        nombre: values.nombre,
        tipoCultivoId: values.tipoCultivoId ?? null,
        lote: values.lote ?? null,
        areaHectareas: values.areaHectareas ?? null,
        fechaSiembra: values.fechaSiembra.format("YYYY-MM-DD"),
        fechaEstimadaCosecha: values.fechaEstimadaCosecha
          ? values.fechaEstimadaCosecha.format("YYYY-MM-DD") : null,
        estado: values.estado ?? "Sembrado",
        productoSemillaId: values.productoSemillaId ?? null,
        cantidadSemilla: values.cantidadSemilla ?? null,
        observaciones: values.observaciones ?? null,
        generarActividadesDesdeTemplate: !esEdicion && generarActividades,
      };

      if (esEdicion) {
        await axios.put(`${API_URL}/Cultivo/${id}`, body);
        setResultado({ tipo: "success", texto: "✅ Cultivo actualizado correctamente." });
      } else {
        const resp = await axios.post(`${API_URL}/Cultivo`, body);
        const nuevoId = resp.data?.id;
        navigate(`/dashboard/cultivos/${nuevoId}`);
      }
    } catch (err) {
      if (err?.errorFields) return;
      const msg = err?.response?.data?.mensaje ?? err?.message ?? "Error al guardar.";
      setResultado({ tipo: "error", texto: `❌ ${msg}` });
    } finally {
      setGuardando(false);
    }
  };

  const actividadesPlantilla = tipoSeleccionado?.actividades ?? [];

  return (
    <div style={{ padding: 16, maxWidth: 800 }}>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/dashboard/cultivos")}>
          Volver
        </Button>
        <Title level={3} style={{ margin: 0 }}>
          {esEdicion ? "✏️ Editar cultivo" : "🌱 Nuevo cultivo"}
        </Title>
      </Space>

      {resultado && (
        <Alert
          type={resultado.tipo}
          message={resultado.texto}
          showIcon closable
          onClose={() => setResultado(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Spin spinning={cargando}>
        <Card>
          <Form form={form} layout="vertical" initialValues={{ estado: "Sembrado" }}>
            <Row gutter={16}>
              <Col xs={24} md={16}>
                <Form.Item label="Nombre del cultivo" name="nombre"
                  rules={[{ required: true, message: "Ingrese el nombre del cultivo." }]}>
                  <Input placeholder="Ej: Maíz lote norte 2026" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Estado" name="estado">
                  <Select>
                    {ESTADOS.map(e => (
                      <Select.Option key={e} value={e}>{ESTADO_LABEL[e]}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item label="Tipo de cultivo (plantilla)" name="tipoCultivoId">
                  <Select
                    allowClear
                    showSearch
                    placeholder="Seleccione el tipo..."
                    optionFilterProp="children"
                    onChange={onTipoCultivoChange}
                  >
                    {tipos.map(t => (
                      <Select.Option key={t.id} value={t.id}>
                        {t.nombre}
                        {t.diasEstimadosCosecha ? ` (≈${t.diasEstimadosCosecha} días)` : ""}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Lote / Parcela" name="lote">
                  <Input placeholder="Ej: Lote Norte, Parcela 3" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item label="Área (hectáreas)" name="areaHectareas">
                  <InputNumber min={0} step={0.1} precision={4} style={{ width: "100%" }}
                    placeholder="Ej: 1.5" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Fecha de siembra" name="fechaSiembra"
                  rules={[{ required: true, message: "Ingrese la fecha de siembra." }]}>
                  <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD"
                    onChange={onFechaSiembraChange} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Fecha estimada de cosecha" name="fechaEstimadaCosecha">
                  <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={16}>
                <Form.Item label="Semilla usada (producto del inventario)" name="productoSemillaId">
                  <Select allowClear showSearch placeholder="Seleccione producto semilla..."
                    optionFilterProp="children">
                    {productos.map(p => (
                      <Select.Option key={p.id} value={p.id}>{p.nombre}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Cantidad de semilla" name="cantidadSemilla">
                  <InputNumber min={0} step={0.5} precision={3} style={{ width: "100%" }}
                    placeholder="Ej: 25.000 kg" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="Observaciones" name="observaciones">
              <TextArea rows={3} maxLength={1000} showCount
                placeholder="Variedad, condiciones del suelo, fuente de agua..." />
            </Form.Item>
          </Form>
        </Card>

        {/* ── Vista previa de actividades de la plantilla ── */}
        {!esEdicion && tipoSeleccionado && actividadesPlantilla.length > 0 && (
          <Card
            style={{ marginTop: 16, borderColor: "#1677ff" }}
            title={
              <Space>
                <BulbOutlined style={{ color: "#1677ff" }} />
                <Text strong style={{ color: "#1677ff" }}>
                  Actividades sugeridas para "{tipoSeleccionado.nombre}"
                </Text>
              </Space>
            }
          >
            <Checkbox
              checked={generarActividades}
              onChange={e => setGenerarActividades(e.target.checked)}
              style={{ marginBottom: 12 }}
            >
              <Text strong>Generar automáticamente estas actividades al guardar</Text>
            </Checkbox>

            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={actividadesPlantilla}
              columns={[
                {
                  title: "#",
                  dataIndex: "orden",
                  width: 40,
                  align: "center",
                },
                {
                  title: "Actividad",
                  key: "actividad",
                  render: (_, r) => (
                    <Tag color={TIPO_COLOR[r.tipoActividad] ?? "default"}>
                      {r.tipoActividad}
                    </Tag>
                  ),
                },
                {
                  title: "Días desde siembra",
                  dataIndex: "diasDespieSiembra",
                  width: 160,
                  align: "center",
                  render: v => `+${v} días`,
                },
                {
                  title: "Fecha estimada",
                  key: "fechaEst",
                  render: (_, r) => {
                    const fecha = form.getFieldValue("fechaSiembra");
                    if (!fecha) return <Text type="secondary">—</Text>;
                    return dayjs(fecha).add(r.diasDespieSiembra, "day").format("DD/MM/YYYY");
                  },
                },
                {
                  title: "Descripción",
                  dataIndex: "descripcion",
                  render: v => v || <Text type="secondary">—</Text>,
                },
              ]}
            />
          </Card>
        )}

        <div style={{ marginTop: 16, textAlign: "right" }}>
          <Space>
            <Button onClick={() => navigate("/dashboard/cultivos")}>Cancelar</Button>
            <Button type="primary" icon={<SaveOutlined />}
              onClick={onGuardar} loading={guardando}>
              {esEdicion ? "Guardar cambios" : "Crear cultivo"}
            </Button>
          </Space>
        </div>
      </Spin>
    </div>
  );
}
